import type { ReactNode } from 'react'

import { cn } from '@/lib/utils'

interface PanelProps {
    children: ReactNode
    className?: string
    id?: string
    as?: 'section' | 'div' | 'article' | 'aside'
    labelledBy?: string
}

interface PanelHeaderProps {
    title: ReactNode
    eyebrow?: ReactNode
    description?: ReactNode
    action?: ReactNode
    className?: string
    id?: string
}

export const Panel = ({ children, className, id, as = 'section', labelledBy }: PanelProps): ReactNode => {
    const Component = as

    return (
        <Component
            id={id}
            aria-labelledby={labelledBy}
            className={cn('min-w-0 rounded-xl border border-border bg-card', className)}
        >
            {children}
        </Component>
    )
}

export const PanelHeader = ({ title, eyebrow, description, action, className, id }: PanelHeaderProps): ReactNode => {
    return (
        <div className={cn('flex flex-wrap items-start justify-between gap-3 border-b border-border px-4 py-3 sm:px-5', className)}>
            <div className='min-w-0'>
                {eyebrow && (
                    <p className='mb-0.5 text-[11px] font-medium uppercase tracking-[0.08em] text-muted-foreground'>
                        {eyebrow}
                    </p>
                )}

                <h2 id={id} className='font-heading text-[15px] font-semibold tracking-tight text-foreground'>
                    {title}
                </h2>

                {description && (
                    <p className='mt-0.5 text-[13px] leading-relaxed text-muted-foreground'>
                        {description}
                    </p>
                )}
            </div>

            {action && <div className='flex shrink-0 items-center gap-2'>{action}</div>}
        </div>
    )
}

export const PanelBody = ({ children, className }: { children: ReactNode, className?: string }): ReactNode => {
    return (
        <div className={cn('px-4 py-4 sm:px-5', className)}>
            {children}
        </div>
    )
}
